import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Status } from 'common/utils/constants';
import { alarmSeverityToIcon, getMaxAlarmFromDevice } from 'common/utils/helpers';
import { isDeviceOnline, isDeviceLocked, isDeviceWiFiEnabled } from 'common/utils/deviceTwinUtils';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import IconButton from 'components/shared/IconButton';
import { LockIcon, LockedIcon, WiFiOffIcon, WiFiOnIcon } from 'components/icons';
import styles from './BundleOverviewControllerActionIconsContainer.module.css';

const BundleOverviewControllerActionIconsContainer = ({
  isGa2,
  deviceType,
  device,
  setTargetDevice,
  setIsLockUnlockModalOpen,
  handleWiFiAction,
  handleLockUnlockAction
}) => {
  const targetDevice = useSelector((store) => {
    if (deviceType === 'pivot') {
      return store.ui.pivotOverviewPage.selectedDevices?.[device?.id];
    } else {
      return store.ui.echoOverviewPage.selectedDevice;
    }
  });
  const isWiFiOn = isDeviceWiFiEnabled(targetDevice);
  const isOnline = isDeviceOnline(targetDevice);
  const isLocked = isDeviceLocked(targetDevice);

  const deviceWiFiStatusName = `SetWifi-${targetDevice?.deviceId}`;
  const deviceWiFiStatus = useSelector((store) => store.statuses?.[deviceWiFiStatusName]);
  const isWiFiActionPending = deviceWiFiStatus === Status.PENDING;

  const deviceLockUnlockStatusName = `LockUnlock-${targetDevice?.deviceId}`;
  const deviceLockUnlockStatus = useSelector((store) => store.statuses?.[deviceLockUnlockStatusName]);
  const isLockUnlockActionPending = deviceLockUnlockStatus === Status.PENDING;

  const wifiActionIconMessage = useMemo(() => {
    if (!isOnline) {
      return `Device must be online to ${isWiFiOn ? 'disable Wi-Fi' : 'enable Wi-Fi'}.`;
    }
    if (isWiFiActionPending) {
      return `${isWiFiOn ? 'Disabling Wi-Fi...' : 'Enabling Wi-Fi...'}`;
    }
    return isWiFiOn ? 'Turn off Wi-Fi' : 'Turn on Wi-Fi';
  }, [isOnline, isWiFiOn, isWiFiActionPending]);

  const lockUnlockActionIconMessage = useMemo(() => {
    if (!isOnline) {
      return `Device must be online to ${isLocked ? 'unlock' : 'lock'} device.`;
    }
    if (isLockUnlockActionPending) {
      return `${isLocked ? 'Unlocking the device...' : 'Locking the device...'}`;
    }
    return isLocked ? 'Unlock the device' : 'Lock the device';
  }, [isOnline, isLocked, isLockUnlockActionPending]);

  const maxAlarm = useMemo(() => getMaxAlarmFromDevice(targetDevice), [targetDevice]);
  const alarmIcon = useMemo(() => alarmSeverityToIcon(maxAlarm), [maxAlarm]);
  const disableSetWifiAction = useMemo(() => !isOnline || isWiFiActionPending, [isOnline, isWiFiActionPending]);
  const disableLockUnlockAction = useMemo(
    () => !isOnline || isLockUnlockActionPending,
    [isOnline, isLockUnlockActionPending]
  );

  const handleSetWifiOnClick = useCallback(
    () => isOnline && !isWiFiActionPending && handleWiFiAction(targetDevice),
    [isOnline, isWiFiActionPending, targetDevice, handleWiFiAction]
  );

  const handleLockUnlockOnClick = useCallback(() => {
    if (isOnline && !isLockUnlockActionPending) {
      setTargetDevice(targetDevice);
      isLocked ? handleLockUnlockAction(targetDevice) : setIsLockUnlockModalOpen(true);
    }
  }, [
    targetDevice,
    isOnline,
    isLocked,
    isLockUnlockActionPending,
    setTargetDevice,
    setIsLockUnlockModalOpen,
    handleLockUnlockAction
  ]);

  return (
    <div className={styles.root}>
      {isGa2 && <SvgAF img={alarmIcon} hover={alarmIcon} tooltip={maxAlarm} />}
      {deviceType === 'pivot' && !isGa2 && (
        <IconButton
          aria-label="setWifi"
          variant={disableSetWifiAction ? null : 'text'}
          color="primary"
          size="small"
          onClick={handleSetWifiOnClick}
          data-tip={wifiActionIconMessage}
        >
          {isWiFiOn ? <WiFiOnIcon className={styles.icon} /> : <WiFiOffIcon className={styles.icon} />}
        </IconButton>
      )}
      {!isGa2 && (
        <IconButton
          aria-label="lock"
          variant={disableLockUnlockAction ? null : 'text'}
          color="primary"
          size="small"
          onClick={handleLockUnlockOnClick}
          data-tip={lockUnlockActionIconMessage}
        >
          {isLocked ? <LockIcon className={styles.icon} /> : <LockedIcon className={styles.icon} />}
        </IconButton>
      )}
    </div>
  );
};

export default BundleOverviewControllerActionIconsContainer;
